import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

/**
 * Marquesina de logos de marcas — COMPONENTE ÚNICO COMPARTIDO.
 *
 * Antes existían DOS implementaciones independientes y divergentes:
 *   - `BrandMarquee`, local en pages/LandingPage.jsx (56px, 40s, logos en negro)
 *   - `BrandScroller`, este archivo, usado en Auth / ForgotPassword /
 *     ResetPassword / StoresPreview (altura automática, 24s, logos a color)
 * Ambas leían la misma config del admin, así que los logos coincidían, pero el
 * estilo nunca podía coincidir. Ese era el bug de inconsistencia reportado.
 * Este componente las reemplaza a las dos (Módulo 1).
 *
 * Reglas del Módulo 1:
 *   - Solo logos, sin texto.
 *   - Logos forzados a negro por filtro automático.
 *   - Excepción por logo: `forceBlack: false` en la config del admin conserva
 *     los colores originales (para marcas cuyo logo se pierde en negro).
 *
 * Props:
 *   @param {Array}  logos   — [{ id, name, imageUrl, active, forceBlack }].
 *                             Si no se pasan, el componente consulta la config
 *                             del admin por su cuenta.
 *   @param {number} speed   — segundos por vuelta completa (default 40).
 *   @param {number} height  — alto de la banda en px (default 56).
 *   @param {number} logoH   — alto de cada logo en px (default 30).
 *   @param {string} borders — 'both' (default) | 'bottom'.
 */

/* Último recurso: solo se usa si no hay NINGÚN logo con imagen disponible,
   ni del admin, ni de las tiendas. Se renderiza como texto —única excepción a
   la regla de "solo logos"— para que la banda no quede vacía. */
const FALLBACK = [
  { id: 'f1', name: 'NIKE',     imageUrl: '' },
  { id: 'f2', name: 'ADIDAS',   imageUrl: '' },
  { id: 'f3', name: 'CONVERSE', imageUrl: '' },
  { id: 'f4', name: 'PUMA',     imageUrl: '' },
  { id: 'f5', name: 'LACOSTE',  imageUrl: '' },
];

const BrandScroller = ({
  logos,
  speed   = 40,
  height  = 56,
  logoH   = 30,
  borders = 'both',
}) => {
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    if (logos && logos.length > 0) return; // ya vienen por prop
    let alive = true;

    // Si el admin no configuró marquesina, usamos los logos reales de las tiendas.
    const loadStoreLogos = () => {
      fetch(`${API_BASE_URL}/api/stores`)
        .then((r) => r.json())
        .then((d) => {
          if (!alive) return;
          const list = Array.isArray(d) ? d : (d?.data || []);
          const storeLogos = list
            .filter((s) => s && s.logo)
            .map((s, i) => ({
              id: `st-${s.id ?? i}`,
              name: s.name || '',
              imageUrl: String(s.logo).startsWith('http')
                ? s.logo
                : `${API_BASE_URL}${String(s.logo).startsWith('/') ? '' : '/'}${s.logo}`,
            }));
          if (storeLogos.length > 0) setFetched(storeLogos);
        })
        .catch(() => {});
    };

    fetch(`${API_BASE_URL}/api/landing-config`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        const sections = d?.data?.sections || [];
        const marquee = sections.find((s) => s.type === 'marquee');
        const adminLogos = (marquee?.data?.logos || d?.data?.logos || [])
          .filter((l) => l && l.active !== false);
        if (adminLogos.length > 0) setFetched(adminLogos);
        else loadStoreLogos();
      })
      .catch(() => { loadStoreLogos(); });
    return () => { alive = false; };
  }, [logos]);

  const source = (logos && logos.length > 0) ? logos : fetched;
  const items  = source && source.length > 0 ? source : FALLBACK;

  // Regla "solo logos": si hay al menos una imagen, mostramos únicamente imágenes.
  // Si no hay ninguna, caemos al nombre en texto para no dejar la banda en blanco.
  const withImages = items.filter((b) => b && b.imageUrl);
  const useImages  = withImages.length > 0;
  const visible    = useImages ? withImages : items;

  if (visible.length === 0) return null;

  /* Se duplica exactamente x2 para que el loop sea continuo y sin hueco. */
  const repeated = [...visible, ...visible];

  const borderStyle = '1px solid rgba(0,0,0,0.06)';

  return (
    <div
      style={{
        position:   'relative',
        overflow:   'hidden',
        background: '#fff',
        height,
        borderTop:    borders === 'both' ? borderStyle : undefined,
        borderBottom: borderStyle,
      }}
    >
      <style>{`
        @keyframes vondi-marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .vondi-marquee-track { animation: none !important; }
        }
      `}</style>

      <div
        className="vondi-marquee-track"
        style={{
          display:    'inline-flex',
          alignItems: 'center',
          gap:        52,
          height:     '100%',
          width:      'max-content',
          whiteSpace: 'nowrap',
          animation:  `vondi-marquee ${speed}s linear infinite`,
        }}
      >
        {repeated.map((brand, idx) => (
          <span
            key={`${brand.id}-${idx}`}
            style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
          >
            {useImages ? (
              <img
                src={brand.imageUrl}
                alt={brand.name || ''}
                loading="lazy"
                style={{
                  height:    logoH,
                  maxWidth:  110,
                  objectFit: 'contain',
                  display:   'block',
                  // Negro por defecto; `forceBlack: false` conserva el color original.
                  filter: brand.forceBlack === false
                    ? 'none'
                    : 'grayscale(100%) brightness(0)',
                }}
              />
            ) : (
              <span
                style={{
                  fontWeight:    800,
                  fontStyle:     'italic',
                  fontSize:      16,
                  letterSpacing: '-0.03em',
                  color:         '#0A0A0A',
                  textTransform: 'uppercase',
                }}
              >
                {brand.name}
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

export default BrandScroller;
